<template>
    <b-modal
        v-model="isVisible"
        size="lg"
        title="Resume Subscription"
        modal-class="vue-need-bootstrap"
        lazy
        centered
        hide-footer
        no-close-on-backdrop
    >
        <template #default="{ cancel }">
            <span v-if="!isShopifySubscription">
                Resume your subscription to continue using our services.
                <span v-if="lastPrice"
                    >You will continue with your previous plan of <b>${{ lastPrice.Amount.toFixed(2) }}</b
                    >.</span
                >
            </span>

            <span v-if="isShopifySubscription">
                Your subscription will be resumed using our
                <a href="https://www.customily.com/new-pricing" target="_blank">new pricing</a> for Shopify
                stores.</span
            >

            <validation-observer v-slot="{ handleSubmit, valid }">
                <b-form @submit.stop.prevent="handleSubmit(resumeSubscriptionSubmit)">
                    <!-- Password -->
                    <validation-provider v-slot="{ valid, errors }" rules="required|min:8" name="Password">
                        <b-form-group
                            id="current-password-input"
                            label-cols="3"
                            content-cols="9"
                            label="Current password"
                            label-for="current-password-input"
                            class="my-3"
                            :invalid-feedback="invalidPassword ? 'Wrong password' : errors[0]"
                            :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                        >
                            <b-form-input
                                id="current-password-input"
                                v-model="password"
                                type="password"
                                :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                                :disabled="resumingSubscription"
                            />
                        </b-form-group>
                    </validation-provider>

                    <div v-show="resumeSubscriptionError" class="error-message w-100 pb-3 text-center">
                        Error resuming subscription
                    </div>

                    <div class="modal-footer row pb-0">
                        <b-button type="reset" @click="cancel"> Cancel </b-button>
                        <loader-btn
                            variant="danger"
                            type="submit"
                            loading-text="Saving"
                            :loading="resumingSubscription"
                            :disabled="!valid || resumingSubscription"
                        >
                            Resume Subscription
                        </loader-btn>
                    </div>
                </b-form>
            </validation-observer>
        </template>
    </b-modal>
</template>

<script lang="ts" setup>
import { BModal } from 'bootstrap-vue';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { getLastPrice, updateSubscription } from '@/api/billing';
import { Price, SubscriptionStatusUpdate } from '@/api/typings/billing';
import { StoreType } from '@/typings/user';
import { AxiosError } from 'axios';
import { Ref, computed, onBeforeMount, ref } from 'vue';
import store from '@/store';

const props = defineProps<{
    value: boolean;
    store: string;
    subscriptionId?: string;
}>();

const emit = defineEmits(['input', 'resumed']);

const password = ref('');
const resumingSubscription = ref(false);
const resumeSubscriptionError = ref(false);
const invalidPassword = ref(false);
const lastPrice: Ref<null | Price> = ref(null);
const isShopifySubscription = ref(false);

const user = computed(() => store.state.user);

const isVisible = computed({
    get(): boolean {
        return props.value;
    },
    async set(value: boolean) {
        if (value) {
            resumeSubscriptionError.value = false;
            invalidPassword.value = false;
            password.value = '';

            if (props.store) {
                try {
                    lastPrice.value = await getLastPrice(props.store);
                } catch (error) {
                    const axiosError = error as AxiosError;
                    lastPrice.value = null;
                    if (axiosError.response?.status !== 400) {
                        window.rumReportError(axiosError);
                    }
                }
            } else {
                console.warn(`No store given on SubscriptionResumeDialog`);
            }
        }
        emit('input', value);
    }
});

onBeforeMount(() => {
    const store = user.value?.Stores.find((store) => store.Url == props.store);
    if (store) {
        isShopifySubscription.value = store.Type === StoreType.Shopify;
    }
});

const resumeSubscriptionSubmit = async () => {
    if (!props.store) {
        console.warn(`No store given on SubscriptionResumeDialog`);
        return;
    }

    try {
        resumingSubscription.value = true;
        if (password.value && props.subscriptionId) {
            resumeSubscriptionError.value = false;
            const result = await updateSubscription(props.store, password.value, SubscriptionStatusUpdate.Active);

            // If we have a confirmation URL we have to redirect the user to confirm the subscription there
            if (result.ConfirmationUrl) {
                location.href = result.ConfirmationUrl;
            } else {
                emit('resumed', props.subscriptionId);
                isVisible.value = false;
            }
        }
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 400) {
            if (axiosError.response.data.Message) {
                if (axiosError.response.data.Message === 'Invalid Password') {
                    invalidPassword.value = true;
                } else {
                    resumeSubscriptionError.value = true;
                }
            }
        } else {
            resumeSubscriptionError.value = true;
            window.rumReportError(axiosError);
        }
    } finally {
        resumingSubscription.value = false;
    }
};
</script>

<style lang="scss" scoped>
.confirmation-message {
    display: flex;
    min-height: 200px;
}
</style>
